import { useState, useRef, useEffect } from "react";
import client from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ROLE_LABEL, onboardingLink } from "../lib/helpers";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { QRCodeCanvas } from "qrcode.react";
import { Copy, Check, Camera, UploadSimple, CreditCard, Trash } from "@phosphor-icons/react";
import ShareRow from "../components/ShareRow";
import { toast } from "sonner";

export default function ProfilePage() {
  const { user, setUser } = useAuth();
  const [name, setName] = useState(user.name || "");
  const [avatar, setAvatar] = useState(user.avatar || "");
  const [baseZip, setBaseZip] = useState(user.base_zip || "");
  const [radius, setRadius] = useState(user.radius_miles ?? 50);
  const [saving, setSaving] = useState(false);
  const [slug, setSlug] = useState(user.slug || "");
  const [slugMsg, setSlugMsg] = useState(null);
  const [savingSlug, setSavingSlug] = useState(false);
  const [copied, setCopied] = useState(false);
  const [bio, setBio] = useState(user.bio || "");
  const [gallery, setGallery] = useState(user.gallery || []);
  const [newImg, setNewImg] = useState("");
  const [savingPage, setSavingPage] = useState(false);
  const isPhotog = user.role === "photographer" || user.role === "dual";
  const isAgent = user.role === "agent";
  const [cards, setCards] = useState([]);
  const [loadingCards, setLoadingCards] = useState(false);
  useEffect(() => {
    if (!isAgent) return;
    setLoadingCards(true);
    client.get("/payments/methods").then(({ data }) => setCards(data.cards || [])).catch(() => {}).finally(() => setLoadingCards(false));
  }, [isAgent]);
  const removeCard = async (id) => {
    try { await client.delete(`/payments/methods/${id}`); setCards((c) => c.filter((x) => x.id !== id)); toast.success("Card removed"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not remove card"); }
  };
  const bookingUrl = onboardingLink(user.role, user.slug);
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const onPickPhoto = () => fileRef.current?.click();
  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) return toast.error("Use a PNG, JPEG or WEBP image");
    if (file.size > 5 * 1024 * 1024) return toast.error("Image must be under 5 MB");
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await client.post(`/users/${user.id}/avatar`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAvatar(r.data.avatar);
      setUser({ ...user, avatar: r.data.avatar });
      toast.success("Profile picture updated");
    } catch (e2) { toast.error(e2.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const galleryFileRef = useRef(null);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const onGalleryFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) return toast.error("Use a PNG, JPEG or WEBP image");
    if (file.size > 5 * 1024 * 1024) return toast.error("Image must be under 5 MB");
    if (gallery.length >= 8) return toast.error("Up to 8 images");
    setUploadingGallery(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await client.post(`/users/${user.id}/gallery-image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const next = [...gallery, r.data.url];
      setGallery(next);
      const r2 = await client.patch("/profile", { gallery: next });
      setUser(r2.data);
      toast.success("Photo uploaded");
    } catch (e2) { toast.error(e2.response?.data?.detail || "Upload failed"); }
    finally { setUploadingGallery(false); }
  };

  const addImg = () => {
    const u = newImg.trim();
    if (!u) return;
    if (gallery.length >= 8) return toast.error("Up to 8 images");
    setGallery([...gallery, u]); setNewImg("");
  };
  const removeImg = (i) => setGallery(gallery.filter((_, idx) => idx !== i));
  const savePage = async () => {
    setSavingPage(true);
    try {
      const r = await client.patch("/profile", { bio, gallery });
      setUser(r.data);
      toast.success("Booking page content saved");
    } catch { toast.error("Could not save"); }
    finally { setSavingPage(false); }
  };

  const checkSlug = async (v) => {
    const s = v.trim().toLowerCase();
    setSlug(s);
    if (!s) { setSlugMsg(null); return; }
    try { const r = await client.get("/slug/check", { params: { slug: s } }); setSlugMsg(r.data); }
    catch { setSlugMsg(null); }
  };

  const saveSlug = async () => {
    setSavingSlug(true);
    try {
      const r = await client.patch("/profile", { slug: slug.trim().toLowerCase() });
      setUser(r.data);
      toast.success("Your booking page is live!");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save link"); }
    finally { setSavingSlug(false); }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(bookingUrl);
    setCopied(true); setTimeout(() => setCopied(false), 1800);
    toast.success("Link copied");
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = { name, avatar };
      if (isPhotog) { body.base_zip = baseZip; body.radius_miles = Number(radius); }
      const r = await client.patch("/profile", body);
      setUser(r.data);
      toast.success("Profile updated");
    } catch {
      toast.error("Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-xl space-y-8" data-testid="profile-page">
      <div className="flex items-center gap-5">
        <div className="relative group">
          <div className="w-20 h-20 rounded-full bg-[#B08D57] flex items-center justify-center text-[#4A0000] text-2xl font-bold overflow-hidden ring-2 ring-[#E9DDC6]">
            {avatar ? <img src={avatar} alt="" className="w-full h-full object-cover" /> : (name || user.email)[0].toUpperCase()}
          </div>
          <button data-testid="profile-avatar-upload" onClick={onPickPhoto} disabled={uploading}
            className="absolute inset-0 rounded-full bg-black/50 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <Camera size={22} weight="fill" />
          </button>
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={onPhoto} className="hidden" data-testid="profile-avatar-file" />
        </div>
        <div>
          <h2 className="font-display text-3xl leading-none">{name || user.email}</h2>
          <Badge className="bg-[#4A0000]/10 text-[#4A0000] border-[#4A0000]/20 mt-2">{ROLE_LABEL[user.role]}</Badge>
        </div>
      </div>

      <div className="border border-[#E9DDC6] rounded-md bg-white p-6 space-y-4">
        <h3 className="font-semibold">Profile details</h3>
        <div>
          <Label>Full name</Label>
          <Input data-testid="profile-name" value={name} onChange={(e) => setName(e.target.value)} className="bg-white border-[#E9DDC6]" />
        </div>
        <div>
          <Label>Email</Label>
          <Input value={user.email} disabled className="bg-[#FAF9F6] border-[#E9DDC6] font-mono-num" />
          <p className="text-xs text-[#5C5A58] mt-1">Email is your sign-in identity and can't be changed here.</p>
        </div>
        <div>
          <Label>Profile image</Label>
          <div className="flex items-center gap-2">
            <Input data-testid="profile-avatar" value={avatar} onChange={(e) => setAvatar(e.target.value)} placeholder="Paste an image URL…" className="bg-white border-[#E9DDC6]" />
            <Button type="button" data-testid="profile-avatar-upload-btn" onClick={onPickPhoto} disabled={uploading} className="bg-[#4A0000] hover:bg-[#5C1A1A] text-white shrink-0"><UploadSimple size={16} className="mr-1" weight="bold" />{uploading ? "Uploading…" : "Upload"}</Button>
          </div>
          <p className="text-xs text-[#5C5A58] mt-1">Paste a URL, or upload a PNG, JPEG or WEBP image (up to 5 MB).</p>
        </div>
        {user.tier && (
          <div>
            <Label>Pay tier</Label>
            <Input value={user.tier} disabled className="bg-[#FAF9F6] border-[#E9DDC6]" />
          </div>
        )}
        <Button data-testid="profile-save" onClick={save} disabled={saving} className="w-full sm:w-auto bg-[#4A0000] hover:bg-[#5C1A1A] text-white">
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>

      {user.role === "admin" && user.slug && (
        <div className="border border-[#E9DDC6] rounded-md bg-white p-6 space-y-4" data-testid="admin-booking-link-section">
          <div>
            <h3 className="font-semibold">Your booking link</h3>
            <p className="text-sm text-[#5C5A58]">Share this link so clients can book a shoot straight into the Reelty queue. New shoots land in the Open Shoots pool for assignment.</p>
          </div>
          <div className="flex items-center gap-2">
            <a href={bookingUrl} target="_blank" rel="noreferrer" data-testid="admin-booking-link" className="text-sm text-[#4A0000] underline underline-offset-2 truncate">{bookingUrl}</a>
            <Button variant="outline" size="sm" onClick={copyLink} data-testid="admin-copy-link" className="border-[#E9DDC6] shrink-0">{copied ? <Check size={15} /> : <Copy size={15} />}</Button>
          </div>
        </div>
      )}

      {isAgent && (
        <div className="border border-[#E9DDC6] rounded-md bg-white p-6 space-y-4" data-testid="payment-methods-section">
          <div>
            <h3 className="font-semibold flex items-center gap-2"><CreditCard size={18} weight="duotone" className="text-[#4A0000]" />Saved cards</h3>
            <p className="text-sm text-[#5C5A58]">Cards you save at checkout appear here for faster one-click payments.</p>
          </div>
          {loadingCards ? (
            <p className="text-sm text-[#8A8782]">Loading…</p>
          ) : cards.length === 0 ? (
            <p className="text-sm text-[#9A968E]" data-testid="no-saved-cards">No saved cards yet. Tick “Save this card” next time you pay.</p>
          ) : (
            <div className="space-y-2" data-testid="saved-cards-list">
              {cards.map((c) => (
                <div key={c.id} data-testid={`profile-card-${c.id}`} className="flex items-center gap-3 border border-[#E9DDC6] rounded-md p-3">
                  <CreditCard size={18} weight="duotone" className="text-[#4A0000]" />
                  <span className="capitalize text-sm font-medium">{c.brand} •••• {c.last4}</span>
                  <span className="text-xs text-[#8A8782]">exp {c.exp_month}/{String(c.exp_year).slice(-2)}</span>
                  <button data-testid={`remove-card-${c.id}`} onClick={() => removeCard(c.id)} className="ml-auto text-red-600 hover:text-red-800" title="Remove card"><Trash size={16} /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {isPhotog && (
        <div className="border border-[#E9DDC6] rounded-md bg-white p-6 space-y-4" data-testid="booking-page-section">
          <div>
            <h3 className="font-semibold">Your booking page</h3>
            <p className="text-sm text-[#5C5A58]">Share this link (or QR) with agents so they can book you directly — you earn a <span className="text-[#4A0000] font-medium">10% commission</span> on every shoot booked through it, on top of your base pay.</p>
          </div>
          <div>
            <Label>Your link</Label>
            <div className="flex items-center gap-2">
              <span className="text-sm text-[#5C5A58] whitespace-nowrap">/p/</span>
              <Input data-testid="slug-input" value={slug} onChange={(e) => checkSlug(e.target.value)} placeholder="jane-doe" maxLength={15} className="bg-white border-[#E9DDC6] font-mono-num" />
              <Button data-testid="slug-save" onClick={saveSlug} disabled={savingSlug || !slug || (slugMsg && !slugMsg.available)} className="bg-[#4A0000] hover:bg-[#5C1A1A] text-white shrink-0">{savingSlug ? "Saving…" : "Save"}</Button>
            </div>
            {slugMsg && <p data-testid="slug-msg" className={`text-xs mt-1 ${slugMsg.available ? "text-green-700" : "text-red-600"}`}>{slugMsg.reason}</p>}
            <p className="text-xs text-[#5C5A58] mt-1">3–15 characters · lowercase letters, numbers, hyphens.</p>
          </div>
          {user.slug && (
            <div className="flex flex-col sm:flex-row items-start gap-5 pt-2 border-t border-[#F0ECE2]">
              <div className="bg-white p-2 rounded-lg border border-[#E9DDC6]">
                <QRCodeCanvas value={bookingUrl} size={128} fgColor="#4A0000" bgColor="#FFFFFF" level="M" />
              </div>
              <div className="flex-1 min-w-0">
                <Label>Live page</Label>
                <div className="flex items-center gap-2">
                  <a href={bookingUrl} target="_blank" rel="noreferrer" data-testid="booking-link" className="text-sm text-[#4A0000] underline underline-offset-2 truncate">{bookingUrl}</a>
                  <Button variant="outline" size="sm" onClick={copyLink} data-testid="copy-link" className="border-[#E9DDC6] shrink-0">{copied ? <Check size={15} /> : <Copy size={15} />}</Button>
                </div>
                <p className="text-xs text-[#5C5A58] mt-2">Agents who book here get an account automatically and you're auto-assigned the shoot.</p>
                <div className="mt-4 pt-4 border-t border-[#F0ECE2]">
                  <ShareRow url={bookingUrl} title={`Book a shoot with ${user.name || "me"}`} />
                </div>
              </div>
            </div>
          )}

          <div className="pt-4 border-t border-[#F0ECE2] space-y-3">
            <div>
              <Label>Short bio</Label>
              <Textarea data-testid="bio-input" value={bio} onChange={(e) => setBio(e.target.value)} maxLength={280} rows={3} placeholder="A sentence or two about your work — this shows on your booking page." className="bg-white border-[#E9DDC6]" />
              <p className="text-xs text-[#5C5A58] mt-1">{bio.length}/280</p>
            </div>
            <div>
              <Label>Sample-work gallery</Label>
              <div className="flex items-center gap-2">
                <Input data-testid="gallery-input" value={newImg} onChange={(e) => setNewImg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addImg())} placeholder="Paste an image URL…" className="bg-white border-[#E9DDC6]" />
                <Button type="button" data-testid="gallery-add" onClick={addImg} variant="outline" className="border-[#E9DDC6] shrink-0">Add</Button>
                <Button type="button" data-testid="gallery-upload" onClick={() => galleryFileRef.current?.click()} disabled={uploadingGallery} className="bg-[#4A0000] hover:bg-[#5C1A1A] text-white shrink-0"><UploadSimple size={16} className="mr-1" weight="bold" />{uploadingGallery ? "Uploading…" : "Upload"}</Button>
                <input ref={galleryFileRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={onGalleryFile} className="hidden" data-testid="gallery-file" />
              </div>
              {gallery.length > 0 && (
                <div className="grid grid-cols-4 gap-2 mt-3" data-testid="gallery-thumbs">
                  {gallery.map((src, i) => (
                    <div key={i} className="relative aspect-square rounded-md overflow-hidden border border-[#E9DDC6] group">
                      <img src={src} alt="" className="w-full h-full object-cover" />
                      <button type="button" data-testid={`gallery-remove-${i}`} onClick={() => removeImg(i)} className="absolute top-1 right-1 w-5 h-5 rounded-full bg-[#4A0000] text-white text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">×</button>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-xs text-[#5C5A58] mt-1">Up to 8 photos. First ones show first.</p>
            </div>
            <Button data-testid="save-page-content" onClick={savePage} disabled={savingPage} className="bg-[#4A0000] hover:bg-[#5C1A1A] text-white">{savingPage ? "Saving…" : "Save bio & gallery"}</Button>
          </div>
        </div>
      )}

      {isPhotog && (
        <div className="border border-[#E9DDC6] rounded-md bg-white p-6 space-y-4" data-testid="coverage-section">
          <div>
            <h3 className="font-semibold">Coverage area</h3>
            <p className="text-sm text-[#5C5A58]">Open shoots within this radius of your base ZIP appear in "Available near you".</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Base ZIP code</Label>
              <Input data-testid="profile-base-zip" value={baseZip} onChange={(e) => setBaseZip(e.target.value)} placeholder="94103" className="bg-white border-[#E9DDC6] font-mono-num" />
            </div>
            <div>
              <Label>Radius (miles)</Label>
              <Input data-testid="profile-radius" type="number" value={radius} onChange={(e) => setRadius(e.target.value)} className="bg-white border-[#E9DDC6] font-mono-num" />
            </div>
          </div>
          <Button data-testid="coverage-save" onClick={save} disabled={saving} className="bg-[#4A0000] hover:bg-[#5C1A1A] text-white">
            {saving ? "Saving…" : "Save coverage"}
          </Button>
        </div>
      )}

      <div className="border border-[#E9DDC6] rounded-md bg-white p-6">
        <h3 className="font-semibold mb-1">Settings</h3>
        <p className="text-sm text-[#5C5A58]">More preferences coming soon.</p>
      </div>
    </div>
  );
}
